/**
 * Tiny, safe arithmetic evaluator for Formula columns.
 *
 * NO `eval` / `new Function` — a hand-written recursive-descent parser over a
 * small grammar:
 *
 *   expr   := term (('+' | '-') term)*
 *   term   := factor (('*' | '/' | '%') factor)*
 *   factor := NUMBER
 *           | '(' expr ')'
 *           | ('-' | '+') factor          // unary sign
 *           | REF                          // {colId}
 *           | FUNC '(' REF ')'             // sum({colId}) | avg | min | max | count
 *   FUNC   := 'sum' | 'avg' | 'min' | 'max' | 'count'
 *   REF    := '{' <colId> '}'
 *   NUMBER := digits with optional '.' fraction
 *
 * Column references resolve through the injected `EvalContext`, which is what
 * connects the pure parser to the live data-view (same-row cells and whole
 * column aggregates). Division/modulo by zero yields 0 so column totals stay
 * finite. A malformed expression throws `FormulaError`.
 */

export type AggFn = 'sum' | 'avg' | 'min' | 'max' | 'count';

const AGG_FNS = new Set<AggFn>(['sum', 'avg', 'min', 'max', 'count']);

export type EvalContext = {
  /** Same-row value of `{colId}`, coerced to a number (missing → 0). */
  ref(colId: string): number;
  /** Aggregate of the whole `colId` column. */
  agg(fn: AggFn, colId: string): number;
};

export class FormulaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormulaError';
  }
}

type Token =
  | { kind: 'num'; value: number }
  | { kind: 'op'; value: '+' | '-' | '*' | '/' | '%' }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'ref'; colId: string }
  | { kind: 'func'; fn: AggFn };

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const n = input.length;
  while (i < n) {
    const c = input[i]!;
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i++;
      continue;
    }
    if (c === '+' || c === '-' || c === '*' || c === '/' || c === '%') {
      tokens.push({ kind: 'op', value: c });
      i++;
      continue;
    }
    if (c === '(') {
      tokens.push({ kind: 'lparen' });
      i++;
      continue;
    }
    if (c === ')') {
      tokens.push({ kind: 'rparen' });
      i++;
      continue;
    }
    if (c === '{') {
      const end = input.indexOf('}', i + 1);
      if (end === -1) throw new FormulaError('Unclosed column reference');
      const colId = input.slice(i + 1, end).trim();
      if (!colId) throw new FormulaError('Empty column reference');
      tokens.push({ kind: 'ref', colId });
      i = end + 1;
      continue;
    }
    if (c >= '0' && c <= '9') {
      let j = i + 1;
      let dot = false;
      while (j < n) {
        const cc = input[j]!;
        if (cc >= '0' && cc <= '9') {
          j++;
        } else if (cc === '.' && !dot) {
          dot = true;
          j++;
        } else {
          break;
        }
      }
      tokens.push({ kind: 'num', value: Number.parseFloat(input.slice(i, j)) });
      i = j;
      continue;
    }
    // identifier — only valid as an aggregate function name
    if (/[a-zA-Z]/.test(c)) {
      let j = i + 1;
      while (j < n && /[a-zA-Z]/.test(input[j]!)) j++;
      const word = input.slice(i, j).toLowerCase();
      if (!AGG_FNS.has(word as AggFn)) {
        throw new FormulaError(`Unknown function "${word}"`);
      }
      tokens.push({ kind: 'func', fn: word as AggFn });
      i = j;
      continue;
    }
    throw new FormulaError(`Unexpected character "${c}"`);
  }
  return tokens;
}

class Parser {
  private pos = 0;

  constructor(
    private readonly tokens: Token[],
    private readonly ctx: EvalContext
  ) {}

  parse(): number {
    if (this.tokens.length === 0) throw new FormulaError('Empty expression');
    const value = this.expr();
    if (this.pos !== this.tokens.length) {
      throw new FormulaError('Unexpected trailing input');
    }
    return value;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private expr(): number {
    let value = this.term();
    let t = this.peek();
    while (t?.kind === 'op' && (t.value === '+' || t.value === '-')) {
      this.pos++;
      const rhs = this.term();
      value = t.value === '+' ? value + rhs : value - rhs;
      t = this.peek();
    }
    return value;
  }

  private term(): number {
    let value = this.factor();
    let t = this.peek();
    while (
      t?.kind === 'op' &&
      (t.value === '*' || t.value === '/' || t.value === '%')
    ) {
      this.pos++;
      const rhs = this.factor();
      if (t.value === '*') {
        value = value * rhs;
      } else if (t.value === '/') {
        value = rhs === 0 ? 0 : value / rhs;
      } else {
        value = rhs === 0 ? 0 : value % rhs;
      }
      t = this.peek();
    }
    return value;
  }

  private factor(): number {
    const t = this.peek();
    if (!t) throw new FormulaError('Unexpected end of expression');

    if (t.kind === 'op' && (t.value === '-' || t.value === '+')) {
      this.pos++;
      const v = this.factor();
      return t.value === '-' ? -v : v;
    }
    if (t.kind === 'num') {
      this.pos++;
      return t.value;
    }
    if (t.kind === 'lparen') {
      this.pos++;
      const v = this.expr();
      const close = this.peek();
      if (close?.kind !== 'rparen') throw new FormulaError('Expected ")"');
      this.pos++;
      return v;
    }
    if (t.kind === 'ref') {
      this.pos++;
      return this.ctx.ref(t.colId);
    }
    if (t.kind === 'func') {
      this.pos++;
      if (this.peek()?.kind !== 'lparen') {
        throw new FormulaError(`Expected "(" after ${t.fn}`);
      }
      this.pos++;
      const arg = this.peek();
      if (arg?.kind !== 'ref') {
        throw new FormulaError(`${t.fn}() expects a column reference`);
      }
      this.pos++;
      if (this.peek()?.kind !== 'rparen') {
        throw new FormulaError(`Expected ")" after ${t.fn}(...`);
      }
      this.pos++;
      return this.ctx.agg(t.fn, arg.colId);
    }
    throw new FormulaError('Unexpected token');
  }
}

export function evaluate(expression: string, ctx: EvalContext): number {
  const tokens = tokenize(expression);
  return new Parser(tokens, ctx).parse();
}

/**
 * Static dependency extraction — which columns the expression reads, and which
 * are read through an aggregate. Used for validation and column-picker UX.
 */
export function parseRefs(expression: string): {
  refs: string[];
  aggs: { fn: AggFn; colId: string }[];
} {
  const refs = new Set<string>();
  const aggs: { fn: AggFn; colId: string }[] = [];
  let tokens: Token[];
  try {
    tokens = tokenize(expression);
  } catch {
    return { refs: [], aggs: [] };
  }
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i]!;
    if (t.kind === 'func') {
      const arg = tokens[i + 2];
      if (tokens[i + 1]?.kind === 'lparen' && arg?.kind === 'ref') {
        aggs.push({ fn: t.fn, colId: arg.colId });
        // The ref belongs to the aggregate (whole-column dep), not a same-row
        // ref — skip it so it isn't also counted in `refs`.
        i += 2;
      }
    } else if (t.kind === 'ref') {
      refs.add(t.colId);
    }
  }
  return { refs: [...refs], aggs };
}

export function aggregate(fn: AggFn, values: number[]): number {
  if (fn === 'count') return values.length;
  if (values.length === 0) return 0;
  if (fn === 'sum') return values.reduce((a, b) => a + b, 0);
  if (fn === 'avg') return values.reduce((a, b) => a + b, 0) / values.length;
  if (fn === 'min') return Math.min(...values);
  return Math.max(...values);
}
